import { FullViewImage } from "../../media/FullViewImage";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

type Props = {
  images: { src: string; alt: string }[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};

const FOCUSABLE = "button:not([disabled]), [href], [tabindex]:not([tabindex='-1'])";

const CollectorImageZoom: React.FC<Props> = ({ images, index, onClose, onIndexChange }) => {
  const open = index != null && Boolean(images[index]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft" && images.length > 1 && index != null) {
        onIndexChange((index - 1 + images.length) % images.length);
        return;
      }
      if (event.key === "ArrowRight" && images.length > 1 && index != null) {
        onIndexChange((index + 1) % images.length);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const nodes = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!nodes.length) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, images.length, onClose, onIndexChange]);

  if (!open || index == null) return null;
  const current = images[index];
  const hasNav = images.length > 1;

  return createPortal(
    <div
      ref={dialogRef}
      className="mc-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Zoomed photo"
    >
      <button type="button" className="mc-lightbox-backdrop" onClick={onClose} aria-label="Close zoom" />
      <div className="mc-lightbox-stage">
        {hasNav && (
          <button
            type="button"
            className="mc-lightbox-nav is-prev"
            onClick={() => onIndexChange((index - 1 + images.length) % images.length)}
            aria-label="Previous photo"
          >
            ‹
          </button>
        )}
        <FullViewImage item={current.src} src={current.src} alt={current.alt} />
        {hasNav && (
          <button
            type="button"
            className="mc-lightbox-nav is-next"
            onClick={() => onIndexChange((index + 1) % images.length)}
            aria-label="Next photo"
          >
            ›
          </button>
        )}
      </div>
      <button type="button" ref={closeRef} className="mc-lightbox-close" onClick={onClose}>
        Close
      </button>
      {hasNav && (
        <p className="mc-lightbox-count">
          {index + 1} / {images.length}
        </p>
      )}
    </div>,
    document.body
  );
};

export default CollectorImageZoom;

